/**
 * Policy for the API read boundary (AMP-593).
 *
 * The strict boundary AMP-585 shipped was rolled back in production: four
 * static audit passes each turned up new schema/wire mismatches and the find
 * rate never converged, because static auditing cannot enumerate data states —
 * a deleted user, a photo attachment, a portfolio-less property. Only
 * production traffic can. So the boundary observes instead of enforcing: it
 * reports what it sees to Sentry and degrades gracefully, and a domain is
 * flipped to strict only once its evidence says it is safe.
 *
 * Flipping a domain to strict is gated on BOTH:
 *   (a) the generated Zod base being accurate for that domain — drf-spectacular
 *       currently types nullable fields non-null, so derived schemas inherit
 *       wrong nullability by construction; and
 *   (b) Sentry being quiet for that `kind` over a meaningful window.
 * Each flip needs a named owner and a date. Do not flip a domain here as a
 * drive-by.
 */

/**
 * Domains parsed strictly (throw on mismatch). Deliberately EMPTY: every domain
 * is report-only until its evidence supports a flip. Add a `kind` here only
 * with an owner and a date, per the gate above.
 */
const strictKinds = new Set<string>();

/** Whether `kind` is enforced strictly rather than observed. */
export function isStrictKind(kind: string): boolean {
  return strictKinds.has(kind);
}

/**
 * Fraction of REPEAT mismatches forwarded to Sentry, per tier.
 *
 * The first occurrence of each distinct fingerprint is always reported, so
 * these rates govern volume, not coverage: a mismatch we have never seen is
 * never dropped. They exist because one common bad shape — every ticket
 * carrying a photo attachment, say — would otherwise flood Sentry on day one,
 * someone would mute the issue, and we would lose the instrument we shipped
 * this for.
 *
 * `relaxed` is sampled harder than `raw` because a relaxed-recovered response
 * is the expected, benign case (a nullability gap we absorbed), while a raw
 * fallthrough means a genuine type error we could not absorb.
 */
export const schemaMismatchSampleRates = {
  relaxed: 0.05,
  raw: 0.25,
};

/** Override the sample rates (test hook / runtime tuning). */
export function configureSchemaMismatchSampling(rates: Partial<typeof schemaMismatchSampleRates>) {
  Object.assign(schemaMismatchSampleRates, rates);
}
